import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../utils/api/axiosInstance";
import { useUserAuth } from "./UserAuthContext";

const FavoritesContext = createContext(null);

/**
 * Loads the signed-in user's full favorites list once (GET /users/me/favorites
 * already returns every favorited product id in a single query) and shares it
 * across every product card, instead of each card independently calling
 * GET /users/me/favorites/:productId (N+1 requests on product grids).
 */
export const FavoritesProvider = ({ children }) => {
  const { user } = useUserAuth();
  const userId = user?.id || user?._id || null;

  const [favoriteIds, setFavoriteIds] = useState(() => new Set());
  const [loaded, setLoaded] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!userId) {
      setFavoriteIds(new Set());
      setLoaded(true);
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    setLoaded(false);

    api
      .get("/users/me/favorites", { signal: controller.signal })
      .then(({ data }) => {
        if (requestId !== requestIdRef.current) return;
        const ids = Array.isArray(data) ? data.map((id) => String(id)) : [];
        setFavoriteIds(new Set(ids));
      })
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }
        if (requestId === requestIdRef.current) {
          setFavoriteIds(new Set());
        }
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoaded(true);
      });

    return () => {
      controller.abort();
    };
  }, [userId]);

  const isFavorite = useCallback(
    (productId) => (productId ? favoriteIds.has(String(productId)) : false),
    [favoriteIds],
  );

  const setFavorite = useCallback((productId, value) => {
    if (!productId) return;
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (value) {
        next.add(String(productId));
      } else {
        next.delete(String(productId));
      }
      return next;
    });
  }, []);

  const toggleFavorite = useCallback(
    async (productId) => {
      if (!productId) return { success: false };
      const currentlyFavorite = favoriteIds.has(String(productId));
      const nextIsFavorite = !currentlyFavorite;

      try {
        if (currentlyFavorite) {
          await api.delete("/users/me/favorites", { data: { productId } });
        } else {
          await api.post("/users/me/favorites", { productId });
        }
        setFavorite(productId, nextIsFavorite);
        return { success: true, isFavorite: nextIsFavorite };
      } catch (err) {
        return { success: false, error: err };
      }
    },
    [favoriteIds, setFavorite],
  );

  const value = useMemo(
    () => ({ loaded, isFavorite, setFavorite, toggleFavorite }),
    [loaded, isFavorite, setFavorite, toggleFavorite],
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavoritesContext = () => useContext(FavoritesContext);

export default FavoritesProvider;
